"use client";

import { type CSSProperties, forwardRef, type InputHTMLAttributes, type ReactNode } from "react";

import { cn } from "@/lib/utils";

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "w"> {
  leftElement?: ReactNode;
  rightElement?: ReactNode;
  errorText?: string;
  hasError?: boolean;
  setHasError?: (hasError: boolean) => void;
  containerClassName?: string;
  containerStyle?: CSSProperties;
  containerProps?: Record<string, unknown>;
  inputClassName?: string;
  variant?: "primary";
  /** @default "44px" */
  height?: string;
  /** @default "200px" */
  minW?: string;
  w?: string;
  pr?: string;
}

const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      leftElement,
      rightElement,
      errorText,
      hasError,
      setHasError,
      containerClassName,
      containerStyle,
      containerProps,
      inputClassName,
      variant = "primary",
      height = "44px",
      minW = "200px",
      w,
      pr,
      className,
      ...props
    },
    ref
  ) => {
    return (
      <div
        className={cn("flex w-full flex-col gap-1", containerClassName)}
        style={{ ...containerStyle, ...(w ? { width: w } : {}) }}
        {...containerProps}
      >
        <div className={cn("relative", className)} style={{ minWidth: minW }}>
          {leftElement && (
            <div className="absolute left-0 top-0 flex h-full items-center pl-3">
              {leftElement}
            </div>
          )}
          <input
            ref={ref}
            className={cn(
              "w-full rounded-4",
              "border-2 border-gray-300",
              "px-4 py-3",
              "text-[15px] leading-[140%] font-[600]",
              "text-gray-900",
              "placeholder:text-gray-700 placeholder:font-[400]",
              "transition-[border-color] duration-200 ease-in-out",
              "focus:border-primary-700 focus:shadow-[0_0_0_1px_var(--color-primary-primary700)]",
              "focus:outline-none",
              "hover:border-gray-400",
              hasError && [
                "border-danger-400",
                "focus:border-danger-500 focus:shadow-[0_0_0_1px_var(--color-danger-red500)]",
                "hover:border-danger-500",
              ],
              leftElement && "pl-11",
              rightElement && !pr && "pr-11",
              inputClassName,
              className,
            )}
            style={{
              height,
              minHeight: height,
              ...(pr ? { paddingRight: pr } : {}),
            }}
            aria-invalid={hasError || undefined}
            onFocus={() => setHasError?.(false)}
            onChange={(e) => {
              setHasError?.(false);
              props.onChange?.(e);
            }}
            {...props}
          />
          {rightElement && (
            <div className="absolute right-0 top-0 flex h-full items-center pr-4 pt-0.5">
              {rightElement}
            </div>
          )}
        </div>
        {hasError && errorText && (
          <p className="mt-1 text-left text-[15px] text-danger-500">
            {errorText}
          </p>
        )}
      </div>
    );
  }
);
Input.displayName = "Input";

export { Input };
